import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  Tooltip,
  FormControlLabel,
  Button,
  Switch,
  InputAdornment
} from "@material-ui/core";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Delete from "@material-ui/icons/Delete";
import { useServiceFeeStyle, StyledButton, StyledIconButton } from "./styles";
import { Controller, useForm} from "react-hook-form";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useHasPermissions from "../../../../hooks/useHasPermissions";
import { useTheme } from "@material-ui/core/styles";
import ColoredLine from "../../../ColoredLine";
import clsx from "clsx";

export const serviceFeeSchema = Yup.object().shape({
  serviceFeeID: Yup.number(),
  entityID: Yup.string().required(),
  name: Yup.string().trim().required("Name is required."),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .min(0.01, "Amount must be greater than or equal to 0.01")
    .max(99999.99, "Amount must be less than or equal to 99999.99")
    .required("Amount is required."),
  applyZeroTransactionFlag: Yup.boolean(),
  rateNames: Yup.array().nullable(),
});

const ServiceFee = ({ index: parentIndex, entityID, onServiceFeeSubmit, onDelete, formatCurrency, ...props }) => {
  const classes = useServiceFeeStyle();
  const { hasPermissions } = useHasPermissions();
  const deleteServiceFeesPermission = hasPermissions(["rates.delete"]);
  const editServiceFeesPermission = hasPermissions(["rates.edit"]);
  const addServiceFeesPermission = hasPermissions(["rates.add"]);
  const theme = useTheme();
  const smallMatch = useMediaQuery(theme.breakpoints.down("sm"));
  const isAssignedToRate = props?.rateNames?.length > 0;
  const defaultValues = {
    serviceFeeID: 0,
    entityID: entityID,
    name: "",
    amount: "",
    applyZeroTransactionFlag: false,
    rateNames: []
  };
  const serviceFeeValues = {...defaultValues, ...props};
  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(serviceFeeSchema),
    defaultValues: serviceFeeValues
  });
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, errors, isValid }
  } = methods;

  useEffect(() => {
    reset(serviceFeeValues);
  }, [
    serviceFeeValues.name,
    serviceFeeValues.amount,
    serviceFeeValues.applyZeroTransactionFlag
  ]);
  

  const handleOnServiceFeeSubmit = async (values) => {
    await onServiceFeeSubmit(values, parentIndex);
  }

  const assignedToRateToolTipTitle = (rateNames) => {
    return `This fee is tied to the following rate(s): ${rateNames.join(", ")}. Please unassign if you want to delete this fee.`;
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleOnServiceFeeSubmit)}>
        <Grid container direction={smallMatch ? "column" : "row"}>
          <Grid
            className={clsx(classes.serviceFeeInfoContainer, `service-fee-info-${props.name ? props.name : 'new-service-fee'}`)}
            container
            direction={smallMatch ? "column" : "row"}
          >
            {smallMatch && (
              <>
                <Grid item xs={12}>
                  <Tooltip
                    title={
                      isAssignedToRate
                        ? assignedToRateToolTipTitle(props.rateNames)
                        : ""
                    }
                    placement="bottom-start"
                  >
                    <StyledButton
                      className={classes.mobileViewDelBtn}
                      variant="contained"
                      color="secondary"
                      disabled={!deleteServiceFeesPermission || isAssignedToRate}
                      data-testid="del-btn"
                      onClick={onDelete}
                    >
                      Delete
                    </StyledButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    className={classes.mobileViewSaveBtn}
                    type="submit"
                    color="primary"
                    variant="contained"
                    data-testid="save-btn"
                    disabled={
                      (!editServiceFeesPermission && !addServiceFeesPermission) ||
                      isSubmitting ||
                      !isValid
                    }
                  >
                    Save
                  </Button>
                </Grid>
              </>
            )}
            <Grid item xs={4} md={3} className={classes.serviceFeeName}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className={classes.textfield}
                    id="serviceFeeName"
                    variant="outlined"
                    label="Fee Name"
                    size="medium"
                    error={errors?.name}
                    helperText={errors?.name?.['message']}
                    inputProps={{
                      "data-testid": "serviceFeeName",
                      "aria-label": "serviceFeeName"
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} md={2} className={classes.serviceFeeAmount}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className={classes.textfield}
                    id="serviceFeeAmount"
                    variant="outlined"
                    label="Fee Amount"
                    error={errors?.amount}
                    helperText={errors?.amount?.['message']}
                    onBlur={(e) => field.onChange(formatCurrency(e.target.value))}
                    InputProps={{
                      inputProps: {
                        "data-testid": "serviceFeeAmount",
                        "aria-label": "serviceFeeAmount"
                      },
                      inputMode: "numeric",
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={2} md={5} className={classes.applyzerotransactionflag}>
              <Controller
                name="applyZeroTransactionFlag"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Apply to $0 Transactions"
                    control={
                      <Switch
                        {...field}
                        name="applyZeroTransactionFlag"
                        checked={field.value}
                        data-checked={field.value}
                        color="primary"
                        inputProps={{
                          "data-testid": "applyZeroTransactionFlag",
                          "aria-label": "applyZeroTransactionFlag"
                        }}
                      />
                    }
                  />
                )}
              />
            </Grid>
            {!smallMatch && (
            <>
              <Grid item xs={1}>
                <Tooltip
                  title={
                    isAssignedToRate
                      ? assignedToRateToolTipTitle(props.rateNames)
                      : ""
                  }
                  placement="bottom-start"
                >
                  <StyledIconButton
                    className={classes.delBtn}
                    disabled={!deleteServiceFeesPermission || isAssignedToRate}
                    data-testid="del-btn"
                    onClick={onDelete}
                  >
                    <Delete />
                  </StyledIconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={1}>
                <Button
                  className={classes.saveBtn}
                  type="submit"
                  color="primary"
                  variant="contained"
                  data-testid="save-btn"
                  disabled={
                    (!editServiceFeesPermission && !addServiceFeesPermission) ||
                    isSubmitting ||
                    !isValid
                  }
                >
                  Save
                </Button>
              </Grid>
            </>
          )}
          </Grid>
        </Grid>

        <ColoredLine className={classes.sectionBreak} />
      </form>
    </>
  );
};

ServiceFee.propTypes = {
  serviceFeeID: PropTypes.number,
  name: PropTypes.string,
  amount: PropTypes.number,
  applyZeroTransactionFlag: PropTypes.bool
};

export default ServiceFee;
